import {DI_TOKENS} from '@/di/di.tokens';
import {TimerStatus} from '@/stores/TimerStore/TimerStore.types';

import {injected} from 'brandi';

import {MAX_USER_ENERGY_NEXT_IN_VALUE} from '@/constants';
import {TimerStore} from '@/stores/TimerStore/TimerStore';
import {UserEnergyStore} from '@/stores/UserStores/UserEnergyStore';
import {GlobalWindow} from '@/types';
import {FormatUtils, TimeFormat} from '@/utils/FormatUtils';

export class TimerUpdateSharedUseCase {
  private intervalID: number;
  private timeFormat: TimeFormat;
  private timerStartTimeInSeconds: number;

  constructor(
    private readonly timerStore: TimerStore,
    private readonly userEnergyStore: UserEnergyStore,
    private readonly globalWindow: GlobalWindow,
  ) {
    this.timeFormat = TimeFormat.Full;
    this.intervalID = -1;
    this.timerStartTimeInSeconds = this.getNowInSeconds();
  }

  public init(): void {
    this.timerStore.init(this.userEnergyStore.getUserNextEnergyInSec(), this.timeFormat);

    this.restart();
    this.userEnergyStore.subscribe(() => this.restart());
  }

  public restart(): void {
    this.stop();
    this.updateTime();
    this.start();
  }

  public updateTime(): void {
    this.timerStartTimeInSeconds = this.getNowInSeconds();

    this.timerStore.updateTimer({
      formattedTime: FormatUtils.formatTime(this.userEnergyStore.getUserNextEnergyInSec(), this.timeFormat),
      seconds: Math.round(this.userEnergyStore.getUserNextEnergyInSec()),
      status: TimerStatus.INITIAL,
    });
  }

  public start(): void {
    this.intervalID = this.globalWindow.setInterval(
      this.tick.bind(this),
      this.timeFormat === TimeFormat.Full || this.timeFormat === TimeFormat.Adaptive ? 1000 : 60000,
    );

    this.timerStore.setTimerRunning();
  }

  public pause(): void {
    this.globalWindow.clearInterval(this.intervalID);

    this.timerStore.setTimerPaused();
  }

  public stop(): void {
    this.globalWindow.clearInterval(this.intervalID);

    this.timerStore.setTimerStopped();
  }

  public destroy(): void {
    this.globalWindow.clearInterval(this.intervalID);
  }

  private tick(): void {
    const now = this.getNowInSeconds();
    const elapsedSeconds = now - this.timerStartTimeInSeconds;
    const seconds = Math.max(this.userEnergyStore.getUserNextEnergyInSec() - Math.round(elapsedSeconds), 0);

    if (seconds === 0) {
      const currentUserEnergyValue = this.userEnergyStore.getUserEnergy().value;
      const currentUserEnergyMaxValue = this.userEnergyStore.getUserEnergy().maxValue;

      if (currentUserEnergyValue + 1 <= currentUserEnergyMaxValue) {
        this.userEnergyStore.updateUserEnergyValue({
          value: currentUserEnergyValue + 1,
          nextIn: MAX_USER_ENERGY_NEXT_IN_VALUE,
        });

        if (currentUserEnergyValue + 1 === currentUserEnergyMaxValue) {
          this.timerStore.setTimerFinished();

          this.globalWindow.clearInterval(this.intervalID);
        } else {
          this.timerStore.updateTimer({
            formattedTime: FormatUtils.formatTime(this.userEnergyStore.getUserNextEnergyInSec(), this.timeFormat),
            seconds: this.userEnergyStore.getUserNextEnergyInSec(),
            status: TimerStatus.RUNNING,
          });
        }
      } else {
        this.timerStore.setTimerFinished();

        this.globalWindow.clearInterval(this.intervalID);
      }
    }

    const displayTime = seconds || this.getMaxUserEnergyInValueInSeconds();

    this.timerStore.updateTimer({
      formattedTime: FormatUtils.formatTime(displayTime, this.timeFormat),
      seconds,
    });
  }

  private getNowInSeconds(): number {
    return Math.floor(this.globalWindow.performance.now() / 1000);
  }

  private getMaxUserEnergyInValueInSeconds(): number {
    return Math.floor(MAX_USER_ENERGY_NEXT_IN_VALUE / 1000);
  }
}

injected(TimerUpdateSharedUseCase, DI_TOKENS.timerStore, DI_TOKENS.userEnergyStore, DI_TOKENS.globalWindow);
